import React, { useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import InputModern from 'components/ui/Input/InputModern';
import { SessionContext } from 'contexts/SessionContext';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';
import { CompanyOnboardingContext } from 'contexts/CompanyOnboardingContext';
import { HiUser } from 'react-icons/hi';
import useDarkMode from 'utils/hooks/useDarkMode';

export default function StepProfile() {
  const { nextStep, setCurrentStepData } = useContext(CompanyOnboardingContext);
  const { user } = useContext(SessionContext);
  const [name, setName] = useState('');
  const { whitelabel } = useWhitelabel();
  const [isDarkMode] = useDarkMode();

  useEffect(() => {
    setName(user?.name || '');
  }, [user]);

  // Update the context with current data
  useEffect(() => {
    setCurrentStepData({ name });
  }, [name, setCurrentStepData]);

  return (
    <>
      <div className="flex flex-col gap-4 md:gap-6 flex-1">
        {/* Animated Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="flex justify-center mb-2 md:mb-4"
        >
          <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-2xl md:rounded-3xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-2xl shadow-blue-500/50">
            <HiUser className="text-3xl md:text-4xl lg:text-5xl text-white" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center"
        >
          <h3 className={`text-2xl md:text-3xl lg:text-4xl font-black mb-2 md:mb-3 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Let's get to know you better!
          </h3>
          <p className={`text-base md:text-lg ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Tell us your name and we'll get started
          </p>
        </motion.div>

        {/* Input */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-2 md:space-y-3"
        >
          <label className={`block text-xs md:text-sm font-bold ${
            isDarkMode ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Your Name
          </label>
          <InputModern 
            name="name"
            placeholder="John Doe"
            className={`!w-full !px-4 !py-3 md:!px-6 md:!py-4 text-base md:text-lg !rounded-xl md:!rounded-2xl !border-2 focus:!border-purple-500 transition-all ${
              isDarkMode
                ? '!bg-white/5 !border-white/10 !text-white placeholder:!text-gray-500'
                : '!bg-gray-50 !border-gray-200 !text-gray-900 placeholder:!text-gray-400'
            }`}
            autoFocus={true}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                nextStep({ name });
              }
            }}
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className={`text-xs md:text-sm ${
              isDarkMode ? 'text-gray-500' : 'text-gray-500'
            }`}
          >
            This will be used to identify you in {whitelabel.name}
          </motion.p>
        </motion.div>

        {/* Visual Separator */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className={`h-px w-full ${
            isDarkMode ? 'bg-gradient-to-r from-transparent via-white/20 to-transparent' : 'bg-gradient-to-r from-transparent via-gray-300 to-transparent'
          }`}
        />

        {/* Fun fact or tip */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5 }}
          className={`p-3 md:p-4 rounded-xl md:rounded-2xl ${
            isDarkMode 
              ? 'bg-blue-500/10 border border-blue-500/20' 
              : 'bg-blue-50 border border-blue-200'
          }`}
        >
          <p className={`text-xs md:text-sm ${
            isDarkMode ? 'text-blue-300' : 'text-blue-700'
          }`}>
            💡 <strong>Tip:</strong> Use your real name so your team can easily recognize you
          </p>
        </motion.div>
      </div>
    </>
  );
}
